import {
    createExercise as createExerciseApi,
    deleteExerciseByTag as deleteExerciseByTagApi,
    getExerciseByIndex as getExerciseByIndexApi,
    searchExercises as searchExercisesApi,
    updateExercise as updateExerciseApi,
} from '../api/exercises';
import { ExerciseRecord, ExerciseSearchFilters, ExerciseSearchItem } from '../api/exercises.interface';
import { DispatchFunction } from '../reducers';
import { addError, addSuccess } from './notifications';
import { EXERCISES_UPDATE } from './types';

const EXERCISE_GENERIC_ERROR_MESSAGE = 'An error occurred while working with exercises.';

interface ExerciseMutationResult {
    success: boolean;
    index?: number;
    message?: string;
}

const resolveExerciseErrorMessage = (error: any, fallbackMessage: string): string => {
    if (error?.userFriendly) {
        return error.message;
    }

    return error?.message || fallbackMessage;
};

const updateSearchResultsWithExercise = (
    items: ExerciseSearchItem[],
    nextItem: ExerciseSearchItem,
): ExerciseSearchItem[] => {
    const matchingItemIndex = items.findIndex((item) => item.index === nextItem.index);

    if (matchingItemIndex === -1) {
        return items;
    }

    return items.map((item) => (item.index === nextItem.index ? nextItem : item));
};

export const searchExercises = (filters: ExerciseSearchFilters) => async (dispatch: DispatchFunction) => {
    dispatch({ type: EXERCISES_UPDATE, payload: { items: [], loadingList: true, searchFilters: filters } });

    try {
        const items = await searchExercisesApi(filters);

        dispatch({ type: EXERCISES_UPDATE, payload: { hasLoadedSearch: true, items, loadingList: false, searchFilters: filters } });
    } catch (error: any) {
        dispatch(addError(resolveExerciseErrorMessage(error, EXERCISE_GENERIC_ERROR_MESSAGE)));
        dispatch({ type: EXERCISES_UPDATE, payload: { hasLoadedSearch: true, items: [], loadingList: false, searchFilters: filters } });
    }
};

export const resetExerciseSearch = () => async (dispatch: DispatchFunction) => {
    dispatch({
        type: EXERCISES_UPDATE,
        payload: {
            hasLoadedSearch: false,
            items: [],
            loadingList: false,
            searchFilters: {},
        },
    });
};

export const loadExerciseByIndex = (index: number) => async (dispatch: DispatchFunction) => {
    dispatch({ type: EXERCISES_UPDATE, payload: { loadingItem: true, selectedIndex: index, selectedItem: undefined } });

    try {
        const item = await getExerciseByIndexApi(index);
        dispatch({ type: EXERCISES_UPDATE, payload: { loadingItem: false, selectedIndex: index, selectedItem: item } });
    } catch (error: any) {
        dispatch(addError(resolveExerciseErrorMessage(error, 'Unable to load the exercise.')));
        dispatch({ type: EXERCISES_UPDATE, payload: { loadingItem: false, selectedIndex: index, selectedItem: undefined } });
    }
};

export const clearSelectedExercise = () => async (dispatch: DispatchFunction) => {
    dispatch({ type: EXERCISES_UPDATE, payload: { loadingItem: false, selectedIndex: undefined, selectedItem: undefined } });
};

export const createExercise = (
    exercise: ExerciseRecord,
    navigate: (path: string) => void,
) => async (dispatch: DispatchFunction): Promise<ExerciseMutationResult> => {
    dispatch({ type: EXERCISES_UPDATE, payload: { savingItem: true } });

    try {
        const response = await createExerciseApi(exercise);

        dispatch({ type: EXERCISES_UPDATE, payload: { savingItem: false, selectedIndex: response.index, selectedItem: response.item } });
        dispatch(addSuccess('Exercise created successfully.'));
        navigate(`/exercises/${response.index}/edit`);

        return {
            success: true,
            index: response.index,
        };
    } catch (error: any) {
        const message = resolveExerciseErrorMessage(error, 'Unable to create the exercise.');

        dispatch(addError(message));
        dispatch({ type: EXERCISES_UPDATE, payload: { savingItem: false } });

        return {
            success: false,
            message,
        };
    }
};

export const updateExercise = (
    index: number,
    exercise: ExerciseRecord,
) => async (dispatch: DispatchFunction, getState: any): Promise<ExerciseMutationResult> => {
    dispatch({ type: EXERCISES_UPDATE, payload: { savingItem: true } });

    try {
        const response = await updateExerciseApi(index, exercise);
        const currentItems = getState().exercises.items as ExerciseSearchItem[];
        const nextItem: ExerciseSearchItem = { ...response.item, index: response.index };

        dispatch({
            type: EXERCISES_UPDATE,
            payload: {
                items: updateSearchResultsWithExercise(currentItems, nextItem),
                savingItem: false,
                selectedIndex: response.index,
                selectedItem: response.item,
            },
        });
        dispatch(addSuccess('Exercise updated successfully.'));

        return {
            success: true,
            index: response.index,
        };
    } catch (error: any) {
        const message = resolveExerciseErrorMessage(error, 'Unable to update the exercise.');

        dispatch(addError(message));
        dispatch({ type: EXERCISES_UPDATE, payload: { savingItem: false } });

        return {
            success: false,
            message,
        };
    }
};

export const deleteExercise = (
    tag: number,
    onSuccess?: () => void,
) => async (dispatch: DispatchFunction, getState: any): Promise<ExerciseMutationResult> => {
    dispatch({ type: EXERCISES_UPDATE, payload: { deletingTag: tag } });

    try {
        await deleteExerciseByTagApi(tag);
        const { items, selectedItem } = getState().exercises;
        const nextItems = (items as ExerciseSearchItem[]).filter((item) => item.tag !== tag);

        dispatch({
            type: EXERCISES_UPDATE,
            payload: {
                deletingTag: undefined,
                items: nextItems,
                selectedIndex: selectedItem?.tag === tag ? undefined : getState().exercises.selectedIndex,
                selectedItem: selectedItem?.tag === tag ? undefined : selectedItem,
            },
        });
        dispatch(addSuccess('Exercise deleted successfully.'));

        if (onSuccess) {
            onSuccess();
        }

        return { success: true };
    } catch (error: any) {
        const message = resolveExerciseErrorMessage(error, 'Unable to delete the exercise.');

        dispatch(addError(message));
        dispatch({ type: EXERCISES_UPDATE, payload: { deletingTag: undefined } });

        return {
            success: false,
            message,
        };
    }
};